import React, { useEffect, useState } from 'react';
import { TypeMaster } from '../../../../../services/apiTypes';
import {
  Box,
  TableCell,
  IconButton,
  TextField,
  Button,
  Checkbox,
  Typography,
  MenuItem,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  DragHandle,
  KeyboardArrowDown,
  KeyboardArrowRight,
  CheckOutlined,
  CloseOutlined,
} from '@mui/icons-material';
import SchemaIcon from '@mui/icons-material/Schema';
import { manageItemStyle } from '../../InspectionItemTable';
import {
  INSPECTION_DISPLAY_TYPE,
  MANAGE_ITEM_MODE,
} from '../../../../../_constants/manageConst';
import { useTranslation } from 'react-i18next';
import { MaxLength } from '../../../../../_constants/maxLengthConst';

type typeRowProps = {
  type: TypeMaster;
  mode: number;
  countItems: number;
  isAdd: boolean;
  isAdmin: boolean;
  stickyPosition: number[];
  isDisabledBtn: boolean;
  isOpenAccordion: boolean;
  onClickAccordion: (id: string) => void;
  onClickAddIcon: (categoryId: string, typeId: string) => void;
  onClickEditIcon: (data: TypeMaster) => void;
  onClickDeleteIcon: (data: TypeMaster) => void;
  onClickOk: (
    typeName: string,
    displayType: number,
    isOptionalAddition: boolean,
  ) => void;
  onClickCancel: () => void;
};

const TypeRow: React.FC<typeRowProps> = ({
  type,
  mode,
  countItems,
  isAdd,
  isAdmin,
  stickyPosition,
  isDisabledBtn,
  isOpenAccordion,
  onClickAccordion,
  onClickAddIcon,
  onClickEditIcon,
  onClickDeleteIcon,
  onClickOk,
  onClickCancel,
}) => {
  const { t } = useTranslation();
  /** 新規追加時の中項目名 */
  const [typeName, setTypeName] = useState('');
  /** 新規追加時の表示パターン */
  const [displayType, setDisplayType] = useState(
    INSPECTION_DISPLAY_TYPE.STANDARD.value,
  );
  /** 新規追加時の地域管理者チェックボックス */
  const [isOptionalAddition, setIsOptionalAddition] = useState(false);

  useEffect(() => {
    setTypeName(type.typeName);
    setDisplayType(type.displayType);
    setIsOptionalAddition(isAdmin ? type.isOptionalAddition : true);
  }, [type]);

  const handleSetDisplayType = (value: string | undefined) => {
    if (value !== undefined) {
      setDisplayType(Number(value));
    }
  };

  return (
    <React.Fragment>
      <TableCell
        sx={[
          manageItemStyle.iconCellCenter,
          manageItemStyle.leftSideSticky,
          { left: stickyPosition[1], verticalAlign: 'top' },
        ]}
        rowSpan={isOpenAccordion ? countItems + 1 : 1}
      >
        {mode === MANAGE_ITEM_MODE.SORT && (
          <IconButton size="small" sx={{ cursor: 'grab', touchAction: 'none' }}>
            <DragHandle fontSize="small" />
          </IconButton>
        )}
      </TableCell>
      <TableCell
        sx={[
          manageItemStyle.leftSideSticky,
          { left: stickyPosition[2], p: '0 0.5rem' },
        ]}
      >
        <Box sx={manageItemStyle.nameBox}>
          {!!countItems ? (
            <IconButton
              size="small"
              onClick={() => {
                onClickAccordion(type.typeId);
              }}
              onPointerDown={(e) => e.stopPropagation()}
              sx={manageItemStyle.iconCellCenter}
            >
              {isOpenAccordion ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
            </IconButton>
          ) : (
            <Box sx={manageItemStyle.emptyBox} />
          )}
          {isAdd ? (
            <TextField
              autoFocus
              fullWidth
              size="small"
              value={typeName}
              name="displayName"
              sx={{ py: 1 }}
              onChange={(e) => setTypeName(e.target.value)}
              onPointerDown={(e) => e.stopPropagation()}
              slotProps={{
                htmlInput: {
                  maxLength: MaxLength.ManageInspectionItem.TYPE_NAME,
                },
              }}
            />
          ) : (
            <Typography sx={manageItemStyle.nameTypography}>
              {type.displayName}
            </Typography>
          )}
        </Box>
      </TableCell>
      <TableCell
        sx={[
          manageItemStyle.leftSideSticky,
          { left: stickyPosition[3], p: 0, whiteSpace: 'nowrap' },
        ]}
        colSpan={3}
      >
        {isAdd ? (
          <TextField
            size="small"
            variant="outlined"
            select
            label={t('LBL_DISPLAY_TYPE')}
            sx={{ borderRadius: '6px', py: '5px', width: '150px' }}
            slotProps={{ inputLabel: { shrink: true, sx: { top: '5px' } } }}
            value={displayType}
          >
            {Object.values(INSPECTION_DISPLAY_TYPE).map((item, i) => (
              <MenuItem
                key={`display_type_${i}`}
                value={item.value}
                onClick={(event) =>
                  handleSetDisplayType(event.currentTarget.dataset.value)
                }
              >
                {t(item.label)}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
            <SchemaIcon sx={{ mr: 1, color: 'grey.800' }} />
            <Typography alignSelf="center">
              {t(
                Object.values(INSPECTION_DISPLAY_TYPE).find(
                  (disp) => disp.value === type.displayType,
                )?.label ?? '',
              )}
            </Typography>
          </Box>
        )}
      </TableCell>
      {isAdmin && (
        <TableCell sx={manageItemStyle.iconCellCenter}>
          <Checkbox
            name="isOptionalAddition"
            disabled={!isAdd}
            checked={isAdd ? isOptionalAddition : type.isOptionalAddition}
            onChange={(e) => setIsOptionalAddition(e.target.checked)}
            sx={{ py: '2px' }}
          />
        </TableCell>
      )}
      <TableCell sx={{ py: 0 }} colSpan={14} />
      <TableCell sx={{ py: 0 }}>{type.memo}</TableCell>
      {mode === MANAGE_ITEM_MODE.VIEW ? (
        <React.Fragment>
          <TableCell
            sx={[manageItemStyle.icon1Sticky, manageItemStyle.iconCellCenter]}
          >
            <IconButton
              size="small"
              sx={manageItemStyle.iconCellCenter}
              onClick={() => onClickAddIcon(type.categoryId, type.typeId)}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <Add fontSize="small" color="primary" />
            </IconButton>
          </TableCell>
          <TableCell
            sx={[manageItemStyle.icon2Sticky, manageItemStyle.iconCellCenter]}
          >
            {(isAdmin || type.isOptionalAddition) && (
              <IconButton
                size="small"
                sx={manageItemStyle.iconCellCenter}
                onClick={() => onClickEditIcon(type)}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <Edit fontSize="small" />
              </IconButton>
            )}
          </TableCell>
          <TableCell
            sx={[manageItemStyle.icon3Sticky, manageItemStyle.iconCellCenter]}
          >
            {/* 子項目がなければ削除アイコンを表示する */}
            {!countItems && (isAdmin || type.isOptionalAddition) && (
              <IconButton
                size="small"
                sx={manageItemStyle.iconCellCenter}
                onClick={() => onClickDeleteIcon(type)}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <Delete fontSize="small" color="error" />
              </IconButton>
            )}
          </TableCell>
        </React.Fragment>
      ) : isAdd ? (
        <TableCell
          sx={[
            manageItemStyle.iconCellCenter,
            manageItemStyle.iconEditingSticky,
          ]}
          colSpan={3}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Button
              size="small"
              sx={[
                manageItemStyle.iconCellCenter,
                manageItemStyle.iconEditing,
                isDisabledBtn
                  ? manageItemStyle.editDisabled
                  : manageItemStyle.editOk,
              ]}
              onClick={() =>
                onClickOk(typeName, displayType, isOptionalAddition)
              }
              onPointerDown={(e) => e.stopPropagation()}
              disabled={isDisabledBtn}
            >
              <CheckOutlined sx={{ fontSize: '16px' }} />
            </Button>
            <Button
              size="small"
              sx={[
                manageItemStyle.iconCellCenter,
                manageItemStyle.iconEditing,
                isDisabledBtn
                  ? manageItemStyle.editDisabled
                  : manageItemStyle.editCancel,
              ]}
              onClick={onClickCancel}
              onPointerDown={(e) => e.stopPropagation()}
              disabled={isDisabledBtn}
            >
              <CloseOutlined sx={{ fontSize: '16px' }} />
            </Button>
          </Box>
        </TableCell>
      ) : (
        <TableCell colSpan={3} />
      )}
    </React.Fragment>
  );
};

export default TypeRow;
